import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import AutoLockSubsection from 'app/pages/loanApplication/AutoLockSubsection';
import ChannelSectionGate from 'app/pages/loanApplication/ChannelSectionGate';
import { LoanEditorSection } from 'app/pages/programs/channels/Channels.constants';
import {
  AutoLockFormsLevelType,
  UnderwritingSubsectionNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import useLoanId from 'hooks/useLoanId';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import useScrollIntoView from 'hooks/useScrollIntoView';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getUnderwritingDataAction } from 'store/loanApplications/actions';
import { selectIsLoanLocked } from 'store/loanApplications/selectors';

import Conditions from './Conditions';
import { ConditionsTestIds } from './Conditions/Conditions.testIds';
import Consent from './Consent';
import DocumentExpirationDates from './DocumentExpirationDates';
import General from './General';
import useConditionDrilldown from './hooks/useConditionDrilldown';
import KeyDates from './KeyDates';
import LoanBundleHistory from './LoanBundleHistory';
import LoanDenial from './LoanDenial';
import Notes from './Notes';
import SuspenseReason from './SuspenseReason';
import { wrapperStyles } from './Underwriting.styles';
import UnderwritingSummary from './UnderwritingSummary';
import UwReports from './UwReports';

const Underwriting = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();

  const loanId = useLoanId();

  const [isError, setIsError] = useState(false);

  const { isManageAllowed: isUnderwritingAccessAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_UNDERWRITING),
  );
  const { isViewAllowed: isReportsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.UNDERWRITING_REPORTS),
  );
  const { isViewAllowed: isLoanDenialVisible } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_DENIAL),
  );

  const isQualificationsDetailsEnabled = useFeatureToggle(FeatureFlag.EnableQualificationDetails);

  const isLoanLocked = useSelector(selectIsLoanLocked);

  const isLoadingOrError = isLoading || isError;

  const isQualificationsDetailsAllowed =
    isUnderwritingAccessAllowed && isQualificationsDetailsEnabled;
  const isGeneralEnabled = isUnderwritingAccessAllowed && isLoanLocked;
  const isLoanDenialEnabled = isUnderwritingAccessAllowed && isLoanDenialVisible;

  const fetchData = useCallback(() => {
    if (!loanId) {
      return;
    }

    setIsError(false);
    setIsLoading(true);

    dispatch(getUnderwritingDataAction({ loanId }))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [loanId, dispatch, setIsLoading]);

  usePendingChangesDependentFetch(fetchData);

  const { drilldown } = useConditionDrilldown();
  const [hasConditionsDrilldown] = useState(!!drilldown);

  const containerRef = useRef(null);
  const { scrollContainerRef, triggerScrollIntoView } = useScrollIntoView(containerRef);

  useEffect(() => {
    if (!hasConditionsDrilldown || isLoadingOrError) {
      return;
    }

    triggerScrollIntoView({
      querySelector: `[data-testid="${ConditionsTestIds.SECTION}"]`,
      block: 'start',
    });
  }, [hasConditionsDrilldown, isLoadingOrError, triggerScrollIntoView]);

  return (
    <>
      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <Box ref={scrollContainerRef} sx={wrapperStyles}>
          {isQualificationsDetailsAllowed && (
            <AutoLockSubsection
              areaName={UnderwritingSubsectionNames.GENERAL}
              levelType={AutoLockFormsLevelType.SUBSECTION}
            >
              <UnderwritingSummary isLoading={isLoadingOrError} />
            </AutoLockSubsection>
          )}
          {isUnderwritingAccessAllowed && (
            <AutoLockSubsection
              areaName={UnderwritingSubsectionNames.NOTES}
              levelType={AutoLockFormsLevelType.SUBSECTION}
            >
              <Notes isParentLoading={isLoadingOrError} />
            </AutoLockSubsection>
          )}
          {isGeneralEnabled && (
            <AutoLockSubsection
              areaName={UnderwritingSubsectionNames.GENERAL}
              levelType={AutoLockFormsLevelType.SUBSECTION}
            >
              <General isLoading={isLoadingOrError} setIsLoading={setIsLoading} />
            </AutoLockSubsection>
          )}
          {isUnderwritingAccessAllowed && (
            <AutoLockSubsection
              areaName={UnderwritingSubsectionNames.DOCUMENT_EXPIRATION_DATES}
              levelType={AutoLockFormsLevelType.SUBSECTION}
            >
              <DocumentExpirationDates isLoading={isLoadingOrError} setIsLoading={setIsLoading} />
            </AutoLockSubsection>
          )}
          {isUnderwritingAccessAllowed && (
            <ChannelSectionGate section={LoanEditorSection.CONDITIONS}>
              <Conditions setIsLoading={setIsLoading} />
            </ChannelSectionGate>
          )}
          <Consent />
          <KeyDates />
          <LoanBundleHistory />
          {isReportsAllowed && <UwReports />}
          {isLoanDenialEnabled && (
            <AutoLockSubsection
              areaName={UnderwritingSubsectionNames.DENIAL_REASONS}
              levelType={AutoLockFormsLevelType.SUBSECTION}
            >
              <LoanDenial />
            </AutoLockSubsection>
          )}
          {isUnderwritingAccessAllowed && <SuspenseReason />}
        </Box>
      </ErrorHandlingLayout>

      {/* <EditModal isLoading={isLoading} /> */}
    </>
  );
};

Underwriting.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Underwriting);
